// API base URL. Set REACT_APP_API_URL in .env.local to point at a different
// backend (e.g. http://localhost:8000/api) without editing or committing code.
export const url =
  process.env.REACT_APP_API_URL || "https://remnutri.nextechltd.in/api";

/**
 * Base URL for uploaded files (banners, documents, carousel images, report
 * attachments).
 *
 * Defaults to the API base, which is confirmed correct for this deployment.
 * Files are uploaded to `POST {apiBase}/upload/{type}` and served back from
 * `GET {apiBase}/files/{type}/{filename}` — verified 18 Aug 2026 by fetching a
 * real file named in the RemNutri Postman collection:
 *
 *     GET /api/files/transformation/1753160894361.jpg -> 200 image/jpeg
 *     GET /api/files/programs/does-not-exist.png      -> 400 (route exists)
 *     GET /api/uploads/anything.png                   -> "Page not found"
 *
 * So a stored path must resolve under `/files/{type}/…`. If a banner value ever
 * looks like `/uploads/…` it is wrong at the source, not here.
 *
 * Do NOT point this at the web root. Verified 18 Aug 2026: the root host answers
 * EVERY path — `.png` included — with `text/html`, because the single-page app's
 * catch-all handles anything that is not the API. An <img> pointed there loads
 * index.html and renders as a broken image, which is exactly what happened when
 * this defaulted to the root.
 *
 * Override with REACT_APP_ASSET_URL if files ever move to a CDN or object store.
 */
export const assetUrl = process.env.REACT_APP_ASSET_URL || url;

/**
 * Turns a stored file path into a URL the browser can load.
 *
 * Handles the shapes the API can return: an absolute URL or data URI (used
 * as-is), a root-relative path, and a bare path with no leading slash. Returns
 * an empty string for a missing value so callers can hand the result straight to
 * ImageWithFallback, which renders its placeholder for empty input.
 *
 * Also guards against a doubled prefix: if a stored path already carries `/api`
 * and the base ends in `/api`, joining them naively would produce `/api/api/…`.
 */
export const assetSrc = (path?: string | null): string => {
  const trimmed = (path ?? "").toString().trim();
  if (trimmed === "") return "";
  if (trimmed.startsWith("data:")) return trimmed;

  if (/^(https?:)?\/\//i.test(trimmed)) {
    /*
     * An absolute URL is normally used as-is — but not when it points at this
     * API's own `/files/…` path on a different host.
     *
     * `GET /about_us` stores its image as
     * `http://172.236.182.251:8000/api/files/about_us/1753160894361.jpg`: a raw
     * origin server IP, over plain HTTP, baked in when the record was written.
     * Verified 4 Sep 2026 — that URL does not resolve at all, while the same
     * file served from the configured base returns 200 image/jpeg:
     *
     *     http://172.236.182.251:8000/api/files/about_us/…  -> unreachable
     *     https://remnutri.nextechltd.in/api/files/about_us/… -> 200 image/jpeg
     *
     * Even had it resolved, an http:// image on an https:// page is blocked as
     * mixed content. So a stored absolute URL whose path is one of ours gets
     * re-pointed at the configured base; anything else (a real CDN, an external
     * image) is still returned untouched.
     */
    try {
      const parsed = new URL(trimmed, window.location.origin);
      const filesAt = parsed.pathname.indexOf("/files/");
      if (filesAt !== -1) {
        return `${assetUrl.replace(/\/+$/, "")}${parsed.pathname.slice(filesAt)}`;
      }
    } catch {
      // Not parseable as a URL — fall through and return it as given.
    }
    return trimmed;
  }

  const base = assetUrl.replace(/\/+$/, "");
  let relative = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (base.endsWith("/api") && relative.startsWith("/api/")) {
    relative = relative.slice(4);
  }

  return `${base}${relative}`;
};
